/**
 * Intent Gap Opportunities Dashboard
 * 
 * Purpose: Display SEO content optimization opportunities based on search intent analysis
 * Backend: Core API (seo-engine-core-api) + ETL (earlystage-batch)
 * Data Source: earlystage.intent_gap_opportunity table
 * 
 * Algorithm (ETL):
 * - Analyzes last 90 days of GSC Performance data
 * - Filters: 10+ impressions, <10% CTR, position 3-20, 3+ word queries
 * - Priority Score = (impressions × 0.5) + (position_potential × 0.3) + (ctr_gap × 0.2)
 * - Detects 11 intent types: how-to, informational, comparison, educational, local, timing, list, cost, guide, problem-solving, other
 * - Generates actionable content suggestions
 * 
 * Intent Types:
 * - how-to: Queries starting with "how to", "how do"
 * - informational: "what is", "what are", "who is"
 * - comparison: "vs", "versus", "compare", "best", "top"
 * - educational: "guide", "tutorial", "learn"
 * - list: "list of", "types of", "examples of"
 * - timing: "when", "how long", "how often"
 * - local: "near me", "in [location]"
 * - cost: "price", "cost", "how much"
 * - guide: "step by step", "instructions"
 * - problem-solving: "fix", "solve", "troubleshoot"
 * - other: Default for unmatched patterns
 * 
 * Target Users:
 * - Content strategists (intent-based content planning)
 * - SEO specialists (on-page optimization)
 * - Writers (content gap filling)
 * 
 * @see Backend ETL: seo-engine-earlystage-batch/IntentGapAnalysisETLService.java
 * @see Backend API: seo-engine-core-api (endpoint to be created)
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Lightbulb,
  TrendingUp,
  Search,
  OpenInNew,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { intentGapService, IntentGapOpportunity } from '../../services/intentGapService';
import { useSiteStore } from '../../store/site.store';

const INTENT_COLORS: Record<string, string> = {
  'how-to': '#2196F3',
  'informational': '#4CAF50',
  'comparison': '#FF9800',
  'educational': '#9C27B0',
  'local': '#F44336',
  'timing': '#00BCD4',
  'list': '#3F51B5',
  'cost': '#FF5722',
  'guide': '#8BC34A',
  'problem-solving': '#E91E63',
  'other': '#9E9E9E',
};

export const IntentGapDashboard: React.FC = () => {
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const siteId = siteIdParam ? parseInt(siteIdParam, 10) : selectedSite?.id;
  const [selectedIntentType, setSelectedIntentType] = useState<string>('ALL');

  /**
   * Fetch intent gap opportunities
   */
  const { data: opportunities, isLoading, error, refetch } = useQuery({
    queryKey: ['intent-gaps', siteId, selectedIntentType],
    queryFn: () =>
      intentGapService.getIntentGapOpportunities(siteId!, {
        intentType: selectedIntentType === 'ALL' ? undefined : selectedIntentType,
        limit: 100,
      }),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });



  if (!siteId) {
    return (
      <Alert severity="warning">
        Please select a site to view intent gap opportunities.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load intent gap opportunities. Please try again later.
      </Alert>
    );
  }

  const intentTypes = Array.from(new Set(opportunities?.map((o) => o.intentType) || []));

  // Calculate summary stats
  const totalOpportunities = opportunities?.length || 0;
  const avgPriorityScore =
    opportunities?.reduce((sum, o) => sum + o.priorityScore, 0) / totalOpportunities || 0;
  const totalImpressions = opportunities?.reduce((sum, o) => sum + o.impressions, 0) || 0;
  const avgPosition =
    opportunities?.reduce((sum, o) => sum + o.position, 0) / totalOpportunities || 0;

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom>
            <Lightbulb sx={{ mr: 1, verticalAlign: 'middle' }} />
            Intent Gap Opportunities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Content optimization opportunities based on search intent analysis
          </Typography>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Opportunities
              </Typography>
              <Typography variant="h4">{totalOpportunities}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Impressions
              </Typography>
              <Typography variant="h4">{totalImpressions.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Avg Priority Score
              </Typography>
              <Typography variant="h4">{avgPriorityScore.toFixed(1)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Avg Position
              </Typography>
              <Typography variant="h4">{avgPosition.toFixed(1)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter */}
      <Box mb={3}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Intent Type</InputLabel>
          <Select
            value={selectedIntentType}
            onChange={(e) => setSelectedIntentType(e.target.value)}
            label="Intent Type"
          >
            <MenuItem value="ALL">All Intent Types</MenuItem>
            {intentTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Opportunities Table */}
      {totalOpportunities === 0 ? (
        <Alert severity="info">
          No intent gap opportunities found. This site either has well-optimized content or
          needs more GSC data (10+ impressions, position 3-20, &lt;10% CTR).
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Query</strong></TableCell>
                <TableCell><strong>Intent</strong></TableCell>
                <TableCell><strong>URL</strong></TableCell>
                <TableCell align="right"><strong>Impressions</strong></TableCell>
                <TableCell align="right"><strong>CTR</strong></TableCell>
                <TableCell align="right"><strong>Position</strong></TableCell>
                <TableCell><strong>Suggestion</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {opportunities?.map((opp) => (
                <TableRow key={opp.id} hover>
                  <TableCell>
                    <Chip
                      label={opp.priorityScore.toFixed(1)}
                      color={opp.priorityScore > 50 ? 'error' : opp.priorityScore > 30 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${opp.clicks} clicks`}>
                      <Box display="flex" alignItems="center">
                        <Search sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{opp.query}</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={opp.intentType}
                      size="small"
                      sx={{
                        backgroundColor: INTENT_COLORS[opp.intentType] || INTENT_COLORS['other'],
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={opp.url}>
                      <Box display="flex" alignItems="center" maxWidth={200}>
                        <Typography variant="body2" noWrap>
                          {opp.url.replace(/^https?:\/\//, '').split('/')[0]}...
                        </Typography>
                        <OpenInNew
                          sx={{ ml: 0.5, fontSize: 14, color: 'text.secondary', cursor: 'pointer' }}
                          onClick={() => window.open(opp.url, '_blank')}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{opp.impressions.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={opp.ctr < 5 ? 'error.main' : 'text.primary'}>
                      {opp.ctr.toFixed(2)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{opp.position.toFixed(1)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={opp.suggestion}>
                      <Box maxWidth={300}>
                        <Typography variant="body2" noWrap>
                          <Lightbulb sx={{ fontSize: 14, mr: 0.5, color: 'warning.main', verticalAlign: 'middle' }} />
                          {opp.suggestion}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Info Footer */}
      <Box mt={3}>
        <Alert severity="info" icon={<Search />}>
          <Typography variant="body2">
            <strong>Data Source:</strong> GSC Performance (last 90 days) • 
            <strong> Criteria:</strong> 10+ impressions, &lt;10% CTR, position 3-20, 3+ word queries •
            <strong> Updated:</strong> Daily at 6:00 AM
          </Typography>
        </Alert>
        
        <Alert severity="success" icon={<Lightbulb />} sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>What are these opportunities?</strong>
          </Typography>
          <Typography variant="body2" component="div">
            The page shows queries that meet ALL these criteria - they're opportunities because:
            <ul style={{ marginTop: 8, marginBottom: 8 }}>
              <li>They get decent traffic (10+ impressions)</li>
              <li>They're NOT converting well (&lt;10% CTR)</li>
              <li>You're ranking okay but not great (position 3-20)</li>
              <li>They're specific searches (3+ words)</li>
            </ul>
            These are missed opportunities where you could improve your content to:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Increase CTR (better titles/descriptions)</li>
              <li>Improve ranking (better content targeting the intent)</li>
              <li>Capture more clicks from existing impressions</li>
            </ul>
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};
